import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';

const SellerOfferingCard = ({ offering, onAddToCart, isAuthenticated, onAuthRequired, isComponent = false, onViewDetails }) => {
  const stockQty = offering.stockQuantity ?? offering.stock_quantity ?? offering.stockAmount ?? offering.stock_amount ?? 0;
  const storeSlug = offering.seller?.business_slug || offering.seller?.id;
  const storeUrl = `/store/${storeSlug}`;
  const navigate = useNavigate();

  const isMfg = 
    offering.is3dManufacturing || 
    offering.isCncCapable || 
    offering.isSelfManufacturing || 
    offering.is_self_manufacturing || 
    !!offering.manufacturer_id || 
    !!offering.manufacturerId || 
    offering.isPainted || 
    offering.is_painted ||
    offering.globalItem?.is3dManufacturing ||
    offering.globalItem?.is_3d_manufacturing ||
    offering.globalItem?.isCncCapable ||
    offering.globalItem?.is_cnc_capable ||
    offering.globalItem?.isPainted ||
    offering.globalItem?.is_painted;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      onAuthRequired('Please log in to add items to your cart.');
      return;
    }

    if (isMfg && onViewDetails) {
      onViewDetails();
      return;
    }
    
    // We pass the exact seller ID along with the item to the cart
    const cartItem = isComponent ? {
      ...offering.globalItem,
      componentId: offering.globalItem.globalComponentId || offering.globalItem.id || offering.componentId,
      sellerId: offering.seller.id,
      sellerComponentId: offering.id,
      globalComponentId: offering.globalItem.globalComponentId || offering.globalItem.id
    } : {
      ...offering.globalItem,
      sellerId: offering.seller.id,
      sellerProductId: offering.id
    };

    onAddToCart(cartItem, isComponent);
  };

  return (
    <div 
      className="group relative bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-soft flex flex-col h-full cursor-pointer"
      onClick={onViewDetails}
    >
      <div className="relative aspect-square bg-muted">
        <Image 
          src={offering.imageUrl || "/assets/images/no_image.png"} 
          alt={offering.globalItem.name}
          className="w-full h-full object-cover"
        />
        {(offering.is3dManufacturing || offering.isCncCapable || stockQty <= 0) ? (
          <div className="absolute top-2 left-2 bg-orange-600 text-white px-2 py-1 rounded-full text-[10px] font-bold shadow-sm flex items-center gap-1">
             <Icon name="Zap" size={10} /> 3D PRINT
          </div>
        ) : (
          <div className="absolute top-2 left-2 bg-success text-success-foreground px-2 py-1 rounded-full text-[10px] font-bold shadow-sm flex items-center gap-1">
             <Icon name="Check" size={10} /> 
             {offering.vinSn ? 'In Stock' : `In Stock (${stockQty})`}
          </div>
        )}
        <div className="absolute top-2 right-2 bg-background/90 text-foreground px-2 py-1 rounded-full text-[10px] font-bold shadow-sm backdrop-blur-sm border border-border capitalize">
          {offering.condition || 'New'}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-auto">
          <div className="flex justify-between items-start mb-1 gap-2">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold line-clamp-2">{offering.custom_name || offering.globalItem.name}</h3>
              {offering.vinSn && (
                <span className="text-[10px] text-gray-500 font-mono mt-1 px-2 py-0.5 bg-gray-100 rounded-sm border border-gray-200 self-start">VIN: {offering.vinSn}</span>
              )}
            </div>
            {(offering.average_rating !== undefined) && (
              <button onClick={(e) => { e.stopPropagation(); navigate(`/product-reviews-ratings?componentId=${offering.id}`); }} className="flex items-center space-x-1 shrink-0 pt-1 hover:opacity-80 transition-opacity cursor-pointer" title="View all ratings and reviews">
                <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                <span className="text-sm font-medium">{(offering.average_rating || 0)?.toFixed(1)}</span>
              </button>
            )}
          </div>
          
          {(offering.globalItem.manufacturer || offering.globalItem.manufacturerName) && (
            <span className="text-muted-foreground font-medium text-xs block uppercase mb-3 tracking-wider">
                Mfg: {(() => {
                  const name = typeof offering.globalItem.manufacturer === 'object' ? offering.globalItem.manufacturer?.name : (offering.globalItem.manufacturer || offering.globalItem.manufacturerName);
                  return name === 'SELF' ? (offering.seller?.business_name || 'Direct Seller') : name;
                })()}
            </span>
          )}
          
          <div className="flex flex-col items-start gap-1 mb-3">
            <button onClick={(e) => { e.stopPropagation(); navigate(storeUrl); }} className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors bg-primary/5 px-2 py-1 rounded-md">
                <Icon name="Store" size={14} />
                <span className="font-semibold text-sm">{offering.seller?.business_name || 'Direct Seller'}</span>
              </button>
            
            <button onClick={(e) => { e.stopPropagation(); navigate(`/seller-reviews-ratings?sellerId=${offering.seller?.business_slug || offering.seller?.id}`); }} className="flex items-center text-xs text-muted-foreground hover:text-primary transition-colors pl-1">
                <Icon name="Star" size={12} className="mr-1 text-yellow-500 fill-current" />
                {(() => {
                  const ratingVal = offering.seller?.averageRating ?? offering.seller?.average_rating;
                  const countVal = offering.seller?.totalRatings ?? offering.seller?.total_ratings ?? 0;
                  if (ratingVal !== undefined && ratingVal > 0) {
                    return (
                      <span className="flex items-center gap-1 font-bold">
                        <span>{parseFloat(ratingVal).toFixed(1)}</span>
                        <span className="text-[10px] text-muted-foreground font-medium">({countVal} {countVal === 1 ? 'review' : 'reviews'})</span>
                      </span>
                    );
                  }
                  return <span className="font-medium text-gray-400">No reviews yet</span>;
                })()}
              </button>
          </div>
        </div>

        <div className="mt-auto pt-4 flex items-end justify-between shrink-0">
          <div className="font-bold text-lg text-foreground">
            {formatPrice(offering.price)}
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleAdd}
            iconName="ShoppingCart"
            className="shrink-0 ml-2"
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SellerOfferingCard;
